import type { Unit } from './types.js';

export const ML_PER_OZ = 29.5735;

export function ozToMl(oz: number): number {
  return oz * ML_PER_OZ;
}

export function mlToOz(ml: number): number {
  return ml / ML_PER_OZ;
}

/** Volumetric units only; dashes, drops etc. have no fixed volume here. */
export function isVolumeUnit(unit: Unit): unit is 'oz' | 'ml' {
  return unit === 'oz' || unit === 'ml';
}

/** Converts an oz/ml amount to ml. Returns null for non-volume units. */
export function toMl(amount: number, unit: Unit): number | null {
  if (unit === 'ml') return amount;
  if (unit === 'oz') return ozToMl(amount);
  return null;
}

/** Converts between oz and ml; other units pass through unchanged. */
export function convertAmount(amount: number, from: Unit, to: 'oz' | 'ml'): number {
  if (from === to || !isVolumeUnit(from)) return amount;
  return to === 'ml' ? ozToMl(amount) : mlToOz(amount);
}

/**
 * Parses what a bartender types: "2", "0.75", ".5", "3/4", "1 1/2". Returns null if unusable.
 * Commas are treated as decimal points ("0,75").
 */
export function parseAmount(input: string): number | null {
  const s = input.trim().replace(',', '.');
  if (!s) return null;
  const mixed = /^(\d+)\s+(\d+)\/(\d+)$/.exec(s);
  if (mixed) {
    const d = Number(mixed[3]);
    return d === 0 ? null : Number(mixed[1]) + Number(mixed[2]) / d;
  }
  const frac = /^(\d+)\/(\d+)$/.exec(s);
  if (frac) {
    const d = Number(frac[2]);
    return d === 0 ? null : Number(frac[1]) / d;
  }
  if (!/^\d*\.?\d+$|^\d+\.$/.test(s)) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

/** Display form: up to 2 decimals, no trailing zeros ("0.75", "2", "22.5"). */
export function formatAmount(n: number, maxDecimals = 2): string {
  return String(Number(n.toFixed(maxDecimals)));
}
